import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';

import { AuthenticationGuard } from './core/services/authentication/authentication.guard';
import { LabConfigurationGuard } from './shared/guards/lab-configuration.guard';
import { ContactSupportComponent } from './shared/contact-support/contact-support.component';
import { ServerErrorComponent } from './shared/server-error/server-error.component';
import { LatestPolicyGuard } from './shared/guards/latest-policy.guard';
import { LegalInfoComponent } from './shared/legal-info/legal-info.component';
import { MangePatientGuard } from './shared/guards/manage-patients.guard';
import { ManageProjectsGuard } from './shared/guards/manage-projects.guard';
import { ManageAccountsGuard } from './shared/guards/manage-accounts.guard';
import { ManagePackageGuard } from 'src/app/shared/guards/manage-package.guard';
import { MfaVerificationGuard } from './core/services/authentication/mfa-verification.guard';
import { ViewUserActivityLogsGuard } from './shared/guards/view-user-activity-logs.guard';

const routes: Routes = [
  {
    path: 'account',
    loadChildren: () => import('./feature/account-management/account-management.module').then(m => m.AccountManagementModule),
  },
  {
    path: 'home',
    loadChildren: () => import('./feature/home/home.module').then(m => m.HomeModule),
    canLoad: [AuthenticationGuard, LatestPolicyGuard],
    canActivate:[MfaVerificationGuard]
  },
  {
    path: 'announcements',
    loadChildren: () => import('./feature/announcements/announcements.module').then(m => m.AnnouncementsModule),
    canLoad: [AuthenticationGuard, LatestPolicyGuard],
    canActivate:[MfaVerificationGuard]
  },
  {
    path: 'manage-projects',
    loadChildren: () => import('./feature/manage-projects/manage-projects.module').then(m => m.ManageProjectsModule),
    canLoad: [AuthenticationGuard, LatestPolicyGuard, ManageProjectsGuard]
  },
  {
    path: 'manage-accounts',
    loadChildren: () => import('./feature/manage-accounts/manage-accounts.module').then(m => m.ManageAccountsModule),
    canLoad: [AuthenticationGuard, LatestPolicyGuard, ManageAccountsGuard]
  },
  {
    path: 'manage-customer-users',
    loadChildren: () => import('./feature/manage-customer-users/manage-customer-users.module').then(m => m.ManageCustomerUsersModule),
    canLoad: [AuthenticationGuard, LatestPolicyGuard],
    canActivate:[MfaVerificationGuard]
  },
  {
    path: 'manage-internal-users',
    loadChildren: () => import('./feature/manage-internal-users/manage-internal-users.module').then(m => m.ManageInternalUsersModule),
    canLoad: [AuthenticationGuard, LatestPolicyGuard],
    canActivate:[MfaVerificationGuard]
  },
    {
    path: 'user-activity-log',
    loadChildren: () => import('./feature/lab-configuration/audit-logs/audit-logs.module').then(m => m.AuditLogsModule),
    canLoad: [AuthenticationGuard, LatestPolicyGuard, ViewUserActivityLogsGuard],
    canActivate:[MfaVerificationGuard]
  },
  {
    path: 'questions',
    loadChildren: () => import('./feature/lab-configuration/questions/questions.module').then(m => m.QuestionsModule),
    canLoad: [AuthenticationGuard, LatestPolicyGuard, LabConfigurationGuard]
  },
  {
    path: 'patient-list',
    loadChildren: () => import('./feature/manage-patients/bulk-upload/patient-list/patient-list.module').then(m => m.PatientListModule),
    canLoad: [AuthenticationGuard, LatestPolicyGuard, MangePatientGuard]
  },
  // {
  //   path: 'test-panels',
  //   loadChildren: () => import('./feature/lab-configuration/test-panels/test-panels.module').then(m => m.TestPanelsModule),
  //   canLoad: [AuthenticationGuard, LatestPolicyGuard, LabConfigurationGuard]
  // },
  {
    path: 'tests',
    loadChildren: () => import('./feature/lab-configuration/tests/tests.module').then(m => m.TestsModule),
    canLoad: [AuthenticationGuard, LatestPolicyGuard, LabConfigurationGuard],
    canActivate:[MfaVerificationGuard]
  },
  {
    path: 'payors',
    loadChildren: () => import('./feature/lab-configuration/payors/payors.module').then(m => m.PayorsModule),
    canLoad: [AuthenticationGuard, LatestPolicyGuard, LabConfigurationGuard],
    canActivate:[MfaVerificationGuard]
  },
  {
    path: 'update-profile',
    loadChildren: () => import('./feature/update-profile/update-profile.module').then(m => m.UpdateProfileModule),
    canLoad: [AuthenticationGuard, LatestPolicyGuard],
    canActivate:[MfaVerificationGuard]
  },
  {
    path: 'order',
    loadChildren: () => import('./feature/order/order.module').then(m => m.OrderModule),
    canLoad: [AuthenticationGuard, LatestPolicyGuard],
    canActivate:[MfaVerificationGuard]
  },
  {
    path: 'results',
    loadChildren: () => import('./feature/results/results.module').then(m => m.ResultsModule),
    canLoad: [AuthenticationGuard, LatestPolicyGuard],
    canActivate:[MfaVerificationGuard]
  },
  {
    path: 'change-password',
    loadChildren: () => import('./feature/change-password/change-password.module').then(m => m.ChangePasswordModule),
    canLoad: [AuthenticationGuard, LatestPolicyGuard],
    canActivate:[MfaVerificationGuard]
  },
  {
    path: 'email-notification',
    loadChildren: () => import('./feature/email-notification/email-notification.module').then(m => m.EmailNotificationModule),
    canLoad: [AuthenticationGuard, LatestPolicyGuard],
    canActivate:[MfaVerificationGuard]
  },
  {
    path: 'register-labels',
    loadChildren: () => import('./feature/preprinted-label/preprinted-label.module').then(m => m.PreprintedLabelModule),
    canLoad: [AuthenticationGuard, LatestPolicyGuard, ManagePackageGuard],
    canActivate:[MfaVerificationGuard]
  },
  {
    path: 'contact-support',
    component: ContactSupportComponent,
    pathMatch: 'full',
    canActivate: [LatestPolicyGuard]
  },
  {
    path: 'privacy-policy',
    component: LegalInfoComponent,
    pathMatch: 'full',
    canActivate: [LatestPolicyGuard]
  },
  {
    path: 'terms-of-use',
    component: LegalInfoComponent,
    pathMatch: 'full',
    canActivate: [LatestPolicyGuard]
  },
  {
    path: 'error-page',
    component: ServerErrorComponent,
    pathMatch: 'full'
  },
  { path: '', redirectTo: 'home', pathMatch: 'full' },
  { path: '**', redirectTo: 'home', pathMatch: 'full' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
